import EvaluationContext from "./evaluation_context"
import { defaultFormulas, builtInFormulaAliases } from "./formulas"
import { nonAlphaOperatorAliases, alphaOperatorAliases } from "./operators"
import keyWords from "./key_words"
import isSafe from "./is_safe"

export default class ExpressionEngine {
  constructor(getFieldValueCallback, formulas = {}) {
    this.__formulas = { ...defaultFormulas, ...formulas }
    this.__getFieldValue = getFieldValueCallback
  }

  addFormula(name, definition) {
    if (this.__formulas[name]) {
      throw Error(`Formula ${name} is already defined.`)
    }

    this.__formulas[name] = definition
  }

  getFormulaAliases() {
    return {
      ...builtInFormulaAliases,
      ...Object.keys(this.__formulas).reduce(
        (result, el) => ({
          ...result,
          [el]: `fn.${el}`,
        }),
        {}
      ),
    }
  }

  replaceFormulaAliases(exp) {
    const formulaAliases = this.getFormulaAliases()
    const formulaNames = Object.getOwnPropertyNames(formulaAliases)

    const replacer = (match, g1) => {
      return match.replace(g1, formulaAliases[g1])
    }

    return formulaNames.reduce((result, name) => {
      const pattern = new RegExp(`\\b(${name})\\(`, "g")
      return result.replace(pattern, replacer)
    }, exp)
  }

  replaceOperatorAliases(exp, aliases, patternStringFn) {
    const operators = Object.getOwnPropertyNames(aliases)

    const replacer = (match, g1) => {
      return match.replace(g1, aliases[g1])
    }

    return operators.reduce((result, name) => {
      const pattern = new RegExp(patternStringFn(name), "g")
      return result.replace(pattern, replacer)
    }, exp)
  }

  getEvaluationContext(context) {
    return new EvaluationContext(this, context)
  }

  replaceAliases(exp) {
    const alphaPatternStringFn = (name) => `\\b'?\\s?(${name})\\s?'?\\b`
    const nonAlphaPatternStringFn = (name) =>
      `['\\]\\)]?\\s?(${name})\\s?['\\[\\(]?`

    let result = this.replaceFormulaAliases(exp)
    result = this.replaceOperatorAliases(
      result,
      alphaOperatorAliases,
      alphaPatternStringFn
    )
    result = this.replaceOperatorAliases(
      result,
      nonAlphaOperatorAliases,
      nonAlphaPatternStringFn
    )

    return result
  }

  getSafeWords() {
    return [
      ...Object.keys(this.__formulas),
      ...Object.keys(alphaOperatorAliases),
      ...Object.keys(builtInFormulaAliases),
      ...keyWords,
    ]
  }

  isExpSafe(exp) {
    return isSafe(exp, this.getSafeWords())
  }

  evaluate(expression) {
    const context = new EvaluationContext(this)
    return context.evaluate(expression)
  }

  static evaluate(expression, data, namespace) {
    const getValue = (name) => data[!!namespace ? `${namespace}_${name}` : name]

    const engine = new ExpressionEngine(getValue)
    return engine.evaluate(expression)
  }
}
